import { useQuery } from '@tanstack/react-query';
import type {
  PaginatedProviderNoteList,
  PatchedProviderNote,
  ProviderNote,
} from '../../../../../js/api';
import { ProviderNoteService, ProvidersService } from '../../../../../js/api';
import { ActivityLog } from '../../../components/activity-log';
import { PROVIDER_NOTE_CHANNELS } from '../../../components/note-channel-select';
import { SectionHeader } from '../../../components/SectionHeader';
import type {
  ActivityHistory,
  ActivityNote,
  ActivityStatusHistory,
} from '../../../utils/activity-types';
import {
  STAFF_PROVIDER_CHANGE_HISTORY_QUERY_KEY,
  STAFF_PROVIDER_NOTES_QUERY_KEY,
  STAFF_PROVIDER_STATUS_HISTORY_QUERY_KEY,
} from './queryKeys';

/**
 * Activity for the staff provider drawer: staff notes, status changes and the field
 * edits logged from `Provider.audit_fields`. Statuses are read-only here.
 *
 * Notes carry a "Visible to" channel (`ProviderNoteChannel`): Sendit (the default,
 * internal), Clinician (emails the note to the clinician after the backend's grace
 * period) or Draft (only the author sees it).
 */
export function ProviderActivity({ providerId }: { providerId: string }) {
  const { data: notes } = useQuery({
    queryKey: [STAFF_PROVIDER_NOTES_QUERY_KEY, providerId],
    queryFn: () => ProvidersService.providersNotesList({ id: providerId }),
    enabled: !!providerId,
    select: (data: PaginatedProviderNoteList | ProviderNote) => {
      if (!data) return [];
      if (Array.isArray(data)) return data;
      if ('results' in data) return data.results || [];
      return [data as ProviderNote];
    },
  });

  const { data: statusHistory } = useQuery({
    queryKey: [STAFF_PROVIDER_STATUS_HISTORY_QUERY_KEY, providerId],
    queryFn: () =>
      ProvidersService.providersStatusHistoryRetrieve({ id: providerId, sort: 'desc' }),
    enabled: !!providerId,
  });

  const { data: changeHistory } = useQuery({
    queryKey: [STAFF_PROVIDER_CHANGE_HISTORY_QUERY_KEY, providerId],
    queryFn: () =>
      ProvidersService.providersChangeHistoryRetrieve({ id: providerId, sort: 'desc' }),
    enabled: !!providerId,
  });

  return (
    <div
      className="tw:mt-2 tw:p-2 tw:bg-brand-green-3 tw:rounded-lg"
      data-testid="provider-activity"
    >
      <SectionHeader className="tw:mb-2 tw:p-1 tw:text-brand-green-0" headerText="Activity" />
      <ActivityLog
        notes={notes as ActivityNote[] | undefined}
        statusHistory={statusHistory as ActivityStatusHistory[] | undefined}
        changeHistory={changeHistory as ActivityHistory[] | undefined}
        eventHistory={undefined}
        enableCommentsFilter={true}
        enableStatusChangesFilter={true}
        enableUpdatesFilter={true}
        enableEventsFilter={false}
        composerPlaceholder="Enter a comment..."
        noteChannel={PROVIDER_NOTE_CHANNELS}
        noteMutations={{
          createNote: async (data) =>
            ProvidersService.providersNotesCreate({
              id: providerId,
              requestBody: {
                provider: providerId,
                comment: data.comment,
                channel: data.channel,
              } as ProviderNote,
            }),
          updateNote: async (noteId, data) =>
            ProviderNoteService.providerNotePartialUpdate({
              id: noteId,
              requestBody: data as PatchedProviderNote,
            }),
          deleteNote: async (noteId) => ProviderNoteService.providerNoteDestroy({ id: noteId }),
          notesQueryKey: [STAFF_PROVIDER_NOTES_QUERY_KEY, providerId],
          // a note attached to a status change is rendered from the status history
          extraInvalidateQueryKeys: [[STAFF_PROVIDER_STATUS_HISTORY_QUERY_KEY, providerId]],
        }}
      />
    </div>
  );
}
